<template>
  <v-navigation-drawer
    v-if="shouldShow"
    v-model="drawerModel"
    :stateless="true"
    :hide-overlay="!(isOpen && pinned)"
    disable-resize-watcher
    right
    app
    clipped
    temporary
    dark
    color="secondary"
    width="400"
    class="advanced-search-drawer"
  >
    <div
      ref="drawerInner"
      class="advanced-search-drawer__inner"
      tabindex="-1"
      @mousedown="onDrawerMouseDown"
    >
      <tender-filters
        ref="tenderFilters"
        v-show="isOpen"
        :sync-route-on-navigate="syncRouteOnNavigate"
        @close="close"
        @search="submitNavigate"
        @filters-changed="$emit('filters-changed', $event)"
      />
    </div>
  </v-navigation-drawer>
</template>

<script>
import TenderFilters from "./TenderFilters.vue";
import { bus } from "../main.js";

export default {
  name: "AdvancedSearchDrawer",

  components: { TenderFilters },

  props: {
    shouldShow: {
      type: Boolean,
      default: false,
    },
    rightDrawerOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: false,
      pinned: false,
      ignoreClose: false,
      reopenPending: false,
      outsideClickListening: false,
      onOutsidePointerDown: null,
    };
  },

  computed: {
    syncRouteOnNavigate() {
      return !this.isOpen || this.$route.path.includes("/search");
    },

    drawerModel: {
      get() {
        return this.isOpen;
      },
      set(next) {
        if (!next) {
          if (this.ignoreClose) {
            this.$nextTick(() => {
              this.isOpen = true;
            });
            return;
          }
          this.close();
          return;
        }
        this.isOpen = true;
      },
    },
  },

  watch: {
    isOpen(val) {
      if (!val) {
        this.removeOutsideClickListener();
        return;
      }
      this.addOutsideClickListener();
    },
  },

  mounted() {
    this.onHideFilters = (show) => {
      if (show) {
        this.open({ pin: true });
      } else {
        this.close();
      }
    };
    bus.$on("hideFilters", this.onHideFilters);
  },

  beforeDestroy() {
    bus.$off("hideFilters", this.onHideFilters);
    this.removeOutsideClickListener();
  },

  methods: {
    getTenderFilters() {
      return this.$refs.tenderFilters || null;
    },

    getSearchHeaderState() {
      const tf = this.getTenderFilters();
      if (!tf) {
        return {
          keywordChips: [],
          hasAdvancedFilters: false,
          advancedFilterCount: 0,
        };
      }
      return {
        keywordChips: [...(tf.keyword_chips || [])],
        hasAdvancedFilters: tf.hasAdvancedFilters,
        advancedFilterCount: tf.advancedFilterCount,
      };
    },

    addKeywordChip(value) {
      const tf = this.getTenderFilters();
      if (!tf) return;
      tf.addKeywordChip(value);
    },

    removeKeywordChip(item) {
      const tf = this.getTenderFilters();
      if (!tf) return;
      tf.remove_kw(item);
    },

    submitFromHeader(pendingText) {
      const tf = this.getTenderFilters();
      if (!tf) return;
      const trimmed = typeof pendingText === 'string' ? pendingText.trim() : '';
      if (trimmed) {
        tf.addKeywordChip({ key: trimmed });
      }
      tf.submitSearch();
    },

    resetFromHeader() {
      const tf = this.getTenderFilters();
      if (!tf) return;
      tf.reset_filters();
    },

    open({ pin = false } = {}) {
      if (pin) {
        this.pinned = true;
      }
      this.isOpen = true;
      this.$emit("close-utility-drawers");
    },

    close() {
      this.ignoreClose = false;
      this.pinned = false;
      this.isOpen = false;
    },

    ensureOpenPinned() {
      this.reopenPending = false;
      this.ignoreClose = true;
      this.pinned = true;
      this.$emit("close-utility-drawers");
      this.isOpen = false;
      this.$nextTick(() => {
        this.isOpen = true;
        this.$nextTick(() => {
          this.ignoreClose = false;
        });
      });
    },

    submitNavigate(searchParams) {
      this.reopenPending = true;
      this.ignoreClose = true;
      this.pinned = true;
      this.isOpen = true;
      this.$router.push({ path: "/search", query: searchParams }).catch(() => {
        this.reopenPending = false;
        this.ignoreClose = false;
      });
    },

    clickOpen() {
      if (this.isOpen) {
        this.pinned = true;
        return;
      }

      this.ignoreClose = true;
      this.pinned = true;
      this.isOpen = true;
      this.$emit("close-utility-drawers");
      this.$nextTick(() => {
        this.ignoreClose = false;
      });
    },

    openForKeywords() {
      this.clickOpen();
      this.$nextTick(() => {
        this.$nextTick(() => {
          const tf = this.getTenderFilters();
          if (tf) {
            tf.showKeywordsPanel();
          }
        });
      });
    },

    isPointerInDrawer(target) {
      return Boolean(target && target.closest && target.closest(".advanced-search-drawer"));
    },

    isPointerInSearch(target) {
      return Boolean(
        target
        && target.closest
        && (target.closest(".tender-search-wrap") || target.closest(".app-header__search"))
      );
    },

    onDrawerMouseDown() {
      const el = this.$refs.drawerInner;
      if (el && document.activeElement !== el) {
        el.focus({ preventScroll: true });
      }
    },

    addOutsideClickListener() {
      if (this.outsideClickListening) {
        return;
      }
      this.onOutsidePointerDown = (e) => {
        if (!this.isOpen) {
          return;
        }
        const inSearch = this.isPointerInSearch(e.target);
        const inDrawer = this.isPointerInDrawer(e.target);
        const inMenu = e.target.closest && e.target.closest(".menuable__content__active");
        if (inDrawer || inSearch || inMenu) {
          return;
        }
        this.close();
      };
      document.addEventListener("pointerdown", this.onOutsidePointerDown, true);
      this.outsideClickListening = true;
    },

    removeOutsideClickListener() {
      if (!this.outsideClickListening) {
        return;
      }
      document.removeEventListener("pointerdown", this.onOutsidePointerDown, true);
      this.outsideClickListening = false;
    },

    handleRouteChange(to) {
      if (to.path.includes("/unsubscribe") || to.path.includes("/not_found")) {
        this.close();
        return;
      }

      if (to.path.includes("/search") && this.reopenPending) {
        this.ensureOpenPinned();
        return;
      }

      const wantsFilters =
        to.query.showFilter === "true" || to.query.showFilter === true;

      if (wantsFilters) {
        this.open({ pin: true });
      } else if (!to.path.includes("/search")) {
        this.close();
      } else if (!this.pinned && !this.rightDrawerOpen && !this.reopenPending) {
        this.close();
      }
    },
  },
};
</script>

<style>
/*
 * Vuetify 2 only applies clipped top offset when the drawer is not temporary
 * (hasApp is false for temporary drawers). Position below the app header.
 */
.advanced-search-drawer.v-navigation-drawer--fixed {
  top: var(--app-header-height, 64px) !important;
  height: calc(100% - var(--app-header-height, 64px)) !important;
  max-height: calc(100% - var(--app-header-height, 64px)) !important;
}

.advanced-search-drawer >>> .v-navigation-drawer__content {
  height: 100%;
  overflow: hidden;
}

.advanced-search-drawer__inner {
  height: 100%;
  overflow: hidden;
}
</style>
